import React from "react";
import {Col, DatePicker, Modal, Popconfirm, Row, Table, Tooltip} from "antd";
import {DeleteOutlined, EditOutlined, PlusOutlined} from "@ant-design/icons";
import {ColumnsType} from "antd/es/table";
import Utils from "../../../../utils";
import CountryFlag from "../../../shared/display/CountryFlag";
import {FilterValue, SorterResult, TablePaginationConfig} from "antd/lib/table/interface";
import IRestServiceFilters from "../../../../model/interface/api/IRestServiceFilters";
import AntdTableOptionsToRestOptionsService from "../../../../model/service/api/AntdTableOptionsToRestOptionsService";
import IHoliday from "../../../../model/interface/ui/IHoliday";
import HolidaysService from "../../../../model/service/ui/HolidaysService";
import HolidayForm from "./HolidayForm";
import LocaleText from "../../settings/dictionary/LocaleText";
import Button from "../../../shared/button/Button";
import {MomentBuilder} from "../../../../utils/MomentBuilder";
import FilterDropDown from "../../../../utils/Filter/FilterDropDown";
import CountryPicker from "../../../shared/pickers/CountryPicker";

interface IProps {
}

interface IState {
    loading?: boolean
    deleting: string[]
    holidays?: IHoliday[]
    holiday?: Partial<IHoliday>
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null> | null,
    sorter: SorterResult<IHoliday> | SorterResult<IHoliday>[] | null
}

class Holidays extends React.Component<IProps, IState> {

    constructor(props: IProps) {
        super(props)
        this.state = {
            deleting: [],
            pagination: {pageSize: 10, hideOnSinglePage: true, showSizeChanger: true},
            filters: null,
            sorter: [{field: 'createdAt', order: 'ascend'}],
        }
    }

    componentDidMount() {
        this.load();
    }

    load() {
        const {filters, pagination, sorter} = this.state
        this.setState({loading: true})

        const optionFilters: IRestServiceFilters = {}

        const options = AntdTableOptionsToRestOptionsService.parse(pagination, filters, sorter)

        HolidaysService.collectionList({
            ...options,
            filters: {...options.filters, ...optionFilters},
            cache: false,
            depth: 5
        }).then(({results, totalCount}) => {
            this.setState(state => ({
                holidays: results,
                loading: false,
                pagination: {
                    ...state.pagination,
                    total: totalCount
                }
            }))
        })
    }

    delete(language: IHoliday) {
        this.setState(state => ({deleting: [...state.deleting, language.uuid]}))
        HolidaysService.resourceDelete(language.uuid).then(() => {
            this.setState(state => ({deleting: [...state.deleting.filter(v => v !== language.uuid)]}), this.load)
        })
    }

    create = () => {
        this.setCurrent({uuid: Utils.uuid()})
    }

    setCurrent(holiday?: Partial<IHoliday>) {
        this.setState({holiday})
    }

    onFinish = () => {
        this.setCurrent()
        this.load()
    }

    render() {
        const {deleting, holidays, holiday, loading, pagination} = this.state

        const viewColumns: ColumnsType<IHoliday> = [
            {
                title: <LocaleText code={'ui.holiday.date'} fallback={'Datum'}/>,
                dataIndex: 'holidayDate',
                sorter: true,
                render: (date) => MomentBuilder.build(date).format('LL'),
                filterDropdown: ({setSelectedKeys, selectedKeys, confirm, clearFilters}) => {
                    return (
                        <FilterDropDown onReset={clearFilters} onConfirm={() => confirm()}>
                            <DatePicker.YearPicker className={'d-block'}
                                                    value={selectedKeys[0] as any}
                                                    onChange={(date) => setSelectedKeys([
                                                        ...(date ? [date.clone().startOf('year')] : []),
                                                        ...(date ? [date.clone().endOf('year')] : []),
                                                    ] as any)}
                            />
                        </FilterDropDown>
                    )
                },
            },
            {
                title: <LocaleText code={'ui.holiday.title'} fallback={'Titulek'}/>,
                dataIndex: 'label',
                sorter: true
            },
            {
                title: <LocaleText code={'ui.holiday.country-code'} fallback={'Krajina'}/>,
                dataIndex: 'countryCode',
                render: (icon) => icon && <CountryFlag countryCode={icon}/>,
                filterDropdown: ({setSelectedKeys, selectedKeys, confirm, clearFilters}) => {
                    return (
                        <FilterDropDown onReset={clearFilters} onConfirm={() => confirm()}>
                            <CountryPicker value={selectedKeys as any} onChange={value => setSelectedKeys(value as any)}/>
                        </FilterDropDown>
                    )
                },
            },
            {
                title: <Row justify={"end"}>
                    <Tooltip title={<LocaleText code={'general.add'}/>}>
                        <Button size={"small"} icon={<PlusOutlined/>} type={"success"} onClick={() => this.create()}/>
                    </Tooltip>
                </Row>,
                key: 'actions',
                dataIndex: 'actions',
                render: (_, elm) => (
                    <Row className="text-right" justify={"end"} gutter={[8, 8]} wrap={false}>
                        <Col>
                            <Tooltip title={<LocaleText code={'general.edit'}/>}>
                                <Button type="link" icon={<EditOutlined/>} size="small"
                                        onClick={() => this.setCurrent(elm)}/>
                            </Tooltip>
                        </Col>
                        <Col>
                            <Tooltip title={<LocaleText code={'general.delete'}/>}>
                                <Popconfirm title={<LocaleText code={'general.confirm.delete.title'} fallback={'Opravdu smazat?'}/>} onConfirm={() => this.delete(elm)}
                                            okText={<LocaleText code={'general.yes'}/>} cancelText={<LocaleText code={'general.no'}/>}>
                                    <Button loading={deleting.includes(elm.uuid)} type={'link'} danger
                                            icon={<DeleteOutlined/>} size="small"/>
                                </Popconfirm>
                            </Tooltip>
                        </Col>
                    </Row>
                )
            }
        ]
        return (
            <>
                {holiday && (
                    <Modal title={holiday.label || <LocaleText code={'general.new'} fallback={'Nový'}/> } visible={true} footer={false}
                           onCancel={() => this.setCurrent()}>
                        <HolidayForm resource={holiday}
                                      onFinish={this.onFinish}>
                        </HolidayForm>
                    </Modal>
                )}
                <Table
                    loading={loading}
                    childrenColumnName={'x'}
                    pagination={pagination}
                    columns={viewColumns}
                    dataSource={holidays}
                    rowKey={row => row.uuid}
                    onChange={(pagination, filters, sorter) => this.setState({
                        pagination,
                        filters,
                        sorter
                    }, this.load)}
                />
            </>
        )
    }
}

export default Holidays